import { Multiselect, Input, Button, LazyImage } from '@frontend/design_system'
import { onlyNumbersWithOneDotRegex } from 'constants/regex'
import { CROSS_BORDER_POLICY_FORM_VALUES } from 'texts/termsAndConditions'
import { ValueType } from 'ui/molecules/Form/types'
import plus from 'assets/icons/blue700/blue700_plus_20x20.webp'
import trash from 'assets/icons/red600/red600_trash_20x20.webp'
import styles from './styles.module.scss'
import { ICountriesValue } from '../../types'
import { FormErrorMessage, SelectValue } from 'types/form'
import { AUSTRALIA_ID, CANADA_ID, USA_ID } from 'constants/locations'
import { ErrorState } from '../../utils'
import { forwardRef } from 'react'

interface ICountriesProps {
  values: ICountriesValue[]
  onChange: (value: ValueType) => void
  error: FormErrorMessage
  onBlur: VoidFunction
  countries: { label: string; value: string }[]
  states: { label: string; value: string }[]
  errors: ErrorState
  onChangeError: React.Dispatch<React.SetStateAction<ErrorState>>
}

export const Countries = forwardRef<HTMLDivElement, ICountriesProps>(
  (
    {
      values,
      onChange,
      error,
      onBlur,
      countries,
      states,
      errors,
      onChangeError,
    },
    ref
  ) => {
    const handleChange = (
      id: number,
      newValues: Partial<ICountriesValue>,
      field?: keyof ICountriesValue
    ) => {
      onChange(
        values.map((value) =>
          value.id === id
            ? {
                ...value,
                ...newValues,
              }
            : value
        )
      )

      if (field) {
        onChangeError((prev: ErrorState) => ({
          ...prev,
          [id]: {
            ...prev[id],
            [field]: newValues?.[field] ? '' : prev[id]?.[field],
          },
        }))
      }

      if (typeof newValues.price !== 'undefined') {
        onBlur()
      }
    }

    const onAddRow = () => {
      onChange([
        ...values,
        {
          id: Date.now(),
          countryList: [],
          price: '',
          usaStateIds: [],
          state: '',
          province: '',
        },
      ])
      onBlur()
    }

    const onDeleteRow = (id: number) => () => {
      onChange(values.filter((value) => value.id !== id))
      onBlur()
    }

    return (
      <div ref={ref}>
        {values.map(
          ({ id, countryList, price, usaStateIds, state, province }, index) => (
            <div key={id} className={styles.row}>
              <Multiselect
                value={countryList}
                placeholder={
                  CROSS_BORDER_POLICY_FORM_VALUES.COUNTRY_LIST.PLACEHOLDER
                }
                error={Array.isArray(error) ? error[index] : error}
                items={countries}
                onChange={(newCountryList: SelectValue[]) =>
                  handleChange(
                    id,
                    { countryList: newCountryList },
                    'countryList'
                  )
                }
                label={CROSS_BORDER_POLICY_FORM_VALUES.COUNTRY_LIST.LABEL}
                isRequired
              />
              {countryList.find(({ value }) => value === String(USA_ID)) && (
                <div
                  data-form-error={
                    errors[id]?.usaStateIds ? 'error' : undefined
                  }
                >
                  <Multiselect
                    value={usaStateIds}
                    items={states}
                    error={errors[id]?.usaStateIds}
                    placeholder={
                      CROSS_BORDER_POLICY_FORM_VALUES.STATE_LIST.PLACEHOLDER
                    }
                    className={styles['state-list']}
                    tooltip={CROSS_BORDER_POLICY_FORM_VALUES.STATE_LIST.TOOLTIP}
                    onChange={(usaStateIds: SelectValue[]) =>
                      handleChange(id, { usaStateIds }, 'usaStateIds')
                    }
                    label={CROSS_BORDER_POLICY_FORM_VALUES.STATE_LIST.LABEL}
                    isRequired
                  />
                </div>
              )}
              {countryList.find(({ value }) => value === String(CANADA_ID)) && (
                <div
                  data-form-error={errors[id]?.province ? 'error' : undefined}
                >
                  <Input
                    size="large"
                    value={province}
                    data-form-error={error ? 'error' : undefined}
                    maxLength={200}
                    error={errors[id]?.province}
                    onChange={(newProvince: string) => {
                      handleChange(id, { province: newProvince }, 'province')
                    }}
                    placeholder={
                      CROSS_BORDER_POLICY_FORM_VALUES.CANADA_STATE.PLACEHOLDER
                    }
                    tooltip={
                      CROSS_BORDER_POLICY_FORM_VALUES.CANADA_STATE.TOOLTIP
                    }
                    label={CROSS_BORDER_POLICY_FORM_VALUES.CANADA_STATE.LABEL}
                  />
                </div>
              )}
              {countryList.find(
                ({ value }) => value === String(AUSTRALIA_ID)
              ) && (
                <div data-form-error={errors[id]?.state ? 'error' : undefined}>
                  <Input
                    size="large"
                    value={state}
                    data-form-error={error ? 'error' : undefined}
                    maxLength={150}
                    error={errors[id]?.state}
                    placeholder={
                      CROSS_BORDER_POLICY_FORM_VALUES.AUSTRALIA_STATE
                        .PLACEHOLDER
                    }
                    onChange={(newState: string) => {
                      handleChange(id, { state: newState })
                    }}
                    tooltip={
                      CROSS_BORDER_POLICY_FORM_VALUES.AUSTRALIA_STATE.TOOLTIP
                    }
                    label={
                      CROSS_BORDER_POLICY_FORM_VALUES.AUSTRALIA_STATE.LABEL
                    }
                  />
                </div>
              )}
              <div className={styles['row-with-actions']}>
                <div className={styles.price}>
                  <Input
                    size="large"
                    placeholder={
                      CROSS_BORDER_POLICY_FORM_VALUES.PRICE.PLACEHOLDER
                    }
                    value={price}
                    onChange={(newPrice: string) => {
                      const preparedPrice = newPrice.replace(
                        onlyNumbersWithOneDotRegex,
                        ''
                      )
                      handleChange(id, { price: preparedPrice })
                    }}
                    label={CROSS_BORDER_POLICY_FORM_VALUES.PRICE.LABEL}
                  />
                </div>
                {values.length !== 1 && (
                  <div className={styles.action}>
                    <Button
                      label={<LazyImage src={trash} width={20} height={20} />}
                      variant="red"
                      onClick={onDeleteRow(id)}
                      size="large"
                    />
                  </div>
                )}
                {index === values.length - 1 && (
                  <div className={styles.action}>
                    <Button
                      label={<LazyImage src={plus} width={20} height={20} />}
                      variant="secondary-border"
                      onClick={onAddRow}
                      size="large"
                      disabled={countryList.length === countries.length}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>
    )
  }
)
